import React, { useLayoutEffect, useRef } from 'react';
import { SPACING, LAYOUT } from '../constants/constants';
import { DashboardHeader } from '../components/dashboardHeader/DashboardHeader';
import { useIsCompact } from '../hooks/UseIsCompact';
import { setMeasuredContentWidth } from '../hooks/UseContentWidth';

interface DashboardLayoutProps {
  title:    string;
  children: React.ReactNode;
}

/**
 * 대시보드 공통 레이아웃.
 *
 * 구조:
 *   - DashboardHeader: 넓은 폭에서 사이드바(220px) 오른쪽에 fixed 56px 헤더
 *   - .dashboard-main: 헤더(56px) + 사이드바(220px) 여백을 확보한 콘텐츠 영역
 *
 * 좁은 폭(<= 999px)에서는 AppSidebar 가 Drawer 로 접히므로(AppSidebar/DashboardHeader 와
 * 같은 `useIsCompact` 사용) 콘텐츠 왼쪽 여백을 0 으로 두고 패딩만 줄인다 — 예전엔 390px
 * 화면에서 220px 사이드바 자리를 그대로 비워 두고 본문이 122px 로 찌그러졌다.
 *
 * App.css 의 `body.job-running .dashboard-main` 규칙이
 * JobProgressBar 표시 시 추가 여백(44px)을 처리한다.
 *
 * AppSidebar 는 MainApp 에서 렌더링하므로 여기에 포함하지 않는다.
 *
 * 본문 상자 .ct-page(src/layout.css, 디자인 시스템 v2 · 감사 card-controltower#834): 최대 폭 1440 · 사이드바 오른쪽 영역 가운데.
 * 그 안쪽 폭을 ResizeObserver 로 재 useContentWidth 에 넣는다(부품 안 폭 판단은 화면 폭이 아니라 이 값으로). 블록 사이 간격은 아직
 * 화면이 각자 준다(L2~L5 에서 셸 간격으로 옮긴다) — 토대 묶음(L1)은 폭 · 가운데 정렬만 바꾼다.
 */
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ title, children }) => {
  const compact = useIsCompact();
  const pageRef = useRef<HTMLDivElement>(null);

  // 본문 폭을 잰다 — 첫 관찰은 붙자마자 온다. 화면을 떠나면 비운다(다음 화면이 다시 잰다).
  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box && box.width > 0) setMeasuredContentWidth(box.width);
    });
    ro.observe(el);
    return () => { ro.disconnect(); setMeasuredContentWidth(null); };
  }, []);

  return (
    <>
      <DashboardHeader title={title} />
      <div
        className="dashboard-main"
        style={{
          marginLeft: compact ? 0 : LAYOUT.sidebarWidth,
          marginTop: LAYOUT.headerHeight,
          padding: compact ? SPACING.md : SPACING.xl,
          minHeight: `calc(100vh - ${LAYOUT.headerHeight}px)`,
        }}
      >
        <div ref={pageRef} className="ct-page" data-ds="page">{children}</div>
      </div>
    </>
  );
};
